import { FC, PropsWithChildren, Ref, MouseEvent, useMemo } from 'react';
import { Skeleton } from '../../atoms';
import { Document, Page, pdfjs } from 'react-pdf';

type PDFFile = string | File | null;
type PDFMainSide = 'height' | 'width';

type BasePdfSinglePageRendererProps = PropsWithChildren<{
  documentClassName?: string;
  pageClassName?: string;
  pdfFile: PDFFile;
  height: number;
  width: number;
  mainSide?: PDFMainSide;
  /** Page rotation in degrees (0 | 90 | 180 | 270). Forwarded to react-pdf <Page rotate=... />. */
  rotate?: number;
  pageInputRef?: Ref<HTMLDivElement | null>;
  onGetAnnotationsSuccess?: () => void;
  onRenderTextLayerSuccess?: () => void;
  /** Fires when the canvas has finished rendering at the current width/height. */
  onPageRenderSuccess?: () => void;
  onContextMenu?: (e: MouseEvent<HTMLDivElement>) => void;
}>;

export const BasePdfSinglePageRenderer: FC<BasePdfSinglePageRendererProps> = ({
  documentClassName,
  pageClassName,
  pdfFile,
  height,
  width,
  mainSide = 'height',
  rotate,
  pageInputRef,
  onGetAnnotationsSuccess,
  onRenderTextLayerSuccess,
  onPageRenderSuccess,
  onContextMenu,
}) => {
  // Options created inside the component to avoid init issues in production builds. cmaps and fonts come from the CDN.
  const options = useMemo(
    () => ({
      cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`,
      cMapPacked: true,
      standardFontDataUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
    }),
    [],
  );

  // Stable element so react-pdf doesn't remount the skeleton on every render.
  const loadingSkeleton = useMemo(() => <Skeleton height={`${height}px`} width={`${width}px`} />, [height, width]);

  /** fixes issue - https://github.com/wojtekmaj/react-pdf/issues/974 */
  const key: string | null = pdfFile instanceof File ? pdfFile.name : pdfFile;

  /** Keyed by `rotate`, since `<Page>` doesn't always repaint on rotate change. The Document is kept. */
  const pageKey = `page-rot-${rotate ?? 0}`;

  return (
    <Document key={key} file={pdfFile} options={options} loading={loadingSkeleton} className={documentClassName}>
      <Page
        key={pageKey}
        className={pageClassName}
        inputRef={pageInputRef}
        renderMode="canvas"
        pageNumber={1}
        height={mainSide === 'height' ? height : undefined}
        width={mainSide === 'width' ? width : undefined}
        rotate={rotate}
        loading={loadingSkeleton}
        onGetAnnotationsSuccess={onGetAnnotationsSuccess}
        onRenderTextLayerSuccess={onRenderTextLayerSuccess}
        onRenderSuccess={onPageRenderSuccess}
        onContextMenu={onContextMenu}
      />
    </Document>
  );
};
